import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/the-sensus-code";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const theSensusCode: CaseStudy = {
  slug: "the-sensus-code",
  title: "THE SENSUS CODE",
  cardTitle: "Разработка бренд-сайта косметического бренда THE SENSUS CODE",
  subtitle: "Бренд-сайт косметического продукта",
  industry: { label: "Красота и косметология", slug: "beauty" },
  eyebrow: "Корпоративный сайт · Косметический бренд",
  h1: "Разработка бренд-сайта косметики THE SENSUS CODE",
  lead:
    "Разработали бренд-сайт THE SENSUS CODE: продуктовую историю, каталог, состав и аргументацию с научным подходом, а также переходы к покупке на внешних площадках.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт бренда" },

  seo: {
    primaryKeyword: "разработка бренд-сайта косметики",
    secondaryKeywords: [
      "создание сайта бренда косметики",
      "бренд-сайт косметического продукта",
      "разработка сайта skincare бренда",
      "сайт для нового косметического бренда",
    ],
    semanticEntities: [
      "продуктовая история",
      "каталог продукции",
      "состав и активные компоненты",
      "до и после",
      "переход к покупке",
      "научный подход",
    ],
    excludedKeywords: [
      "сыворотка купить",
      "косметика для лица",
      "anti-age косметика",
      "крем для лица",
    ],
    title: "Бренд-сайт THE SENSUS CODE — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт косметического бренда THE SENSUS CODE с продуктовой историей, составами, научным подходом и переходами к покупке.",
    h1: "Разработка бренд-сайта косметики THE SENSUS CODE",
    slug: "the-sensus-code",
    canonicalUrl: `${SITE_URL}/cases/the-sensus-code`,
    ogTitle: "Бренд-сайт косметического бренда THE SENSUS CODE",
    ogDescription:
      "Продуктовая история, состав, научная аргументация и переходы к покупке на бренд-сайте THE SENSUS CODE.",
    ogImage: `${SITE_URL}${dir}/the-sensus-code-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка сайтов",
      to: routes.corporate,
      note: "Бренд-сайт с продуктовой историей",
    },
    relatedIndustry: {
      label: "SEO-продвижение сайтов",
      to: routes.seo,
      note: "Видимость бренда в поиске",
    },
    relatedCases: [
      {
        label: "Интернет-магазин премиального бренда BYRAIN",
        to: "/cases/byrain",
        note: "Каталог и научный подход косметического бренда",
      },
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Каталог и карточка товара для косметики",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен бренд-сайт косметического продукта?",
    text: "Соберём продуктовую историю, состав и научную аргументацию, свяжем сайт с каналами покупки.",
    button: "Обсудить сайт бренда",
  },

  client: {
    name: "THE SENSUS CODE",
    url: "https://thesensuscode.ru/",
    city: "Москва",
    industry: "Косметический бренд",
  },
  year: "2024",
  services: [
    "Дизайн бренд-сайта",
    "Разработка каталога и карточек продукта",
    "Раздел состава и научной аргументации",
    "Переходы к внешним каналам покупки",
  ],
  platform: "Tilda",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.corporate },
  ],
  facts: [
    { label: "Клиент", value: "THE SENSUS CODE" },
    { label: "Тип проекта", value: "Бренд-сайт косметического продукта" },
    { label: "Платформа", value: "Tilda" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Бренд-сайт, раскрывающий продукт, каталог и блоки «до/после»",
      "Раздел состава с активными компонентами и аргументацией применения",
      "Продуктовые формулы и научная подача бренда",
      "Переходы на внешние площадки и магазины для покупки",
      "Форма связи и контакты бренда",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "THE SENSUS CODE — косметический бренд, которому был нужен сайт-витрина: раскрыть продукт, состав и научный подход, а продажи направить на внешние площадки и магазины.",
        "Сайт должен был работать как брендовая история, а не как полноценный интернет-магазин — с акцентом на аргументацию и доверие к продукту.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тактильная брендовая подача",
    paragraphs: [
      "Интерфейс собран на молочной подложке из палитры упаковки — без клише с листьями и каплями.",
      "Packshot и интерфейс работают в одной композиции, продукт и аргументация читаются последовательно.",
    ],
    bullets: [
      "Молочная/тактильная подложка вместо белого фона",
      "Packshot в одной композиции с интерфейсом",
      "Последовательная подача: продукт → состав → покупка",
    ],
  },

  identity: {
    fonts: [
      { name: "Geologica", role: "Заголовки", sample: "THE SENSUS CODE" },
      { name: "Manrope", role: "Текст и интерфейс", sample: "Состав и применение" },
    ],
    colors: [
      { hex: "#F2ECE3", name: "Фон витрины" },
      { hex: "#9C8468", name: "Акцент бренда" },
      { hex: "#211E1A", name: "Текст" },
    ],
  },

  cover: img(
    "the-sensus-code-case-cover",
    "Главная страница бренд-сайта THE SENSUS CODE",
    6,
  ),

  gallery: [
    img("the-sensus-code-product", "Раздел продукта и состава THE SENSUS CODE", 6),
    img("the-sensus-code-before-after", "Блок до/после THE SENSUS CODE", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: продукт, аргументация и переход к покупке.",
      "Переход к каналам покупки на мобильной версии показан в заполненном состоянии.",
    ],
    images: [
      mobile("the-sensus-code-mobile-hero", "Мобильная главная бренд-сайта THE SENSUS CODE"),
      mobile("the-sensus-code-mobile-cta", "Мобильный переход к покупке THE SENSUS CODE"),
    ],
  },

  columnBlocks: [
    {
      id: "brand",
      title: "Брендовая история",
      paragraphs: [
        "Hero и объяснение концепции продукта собраны в одном визуальном блоке — сайт сразу вводит в идею бренда.",
      ],
      bullets: ["Hero с packshot", "Концепция продукта"],
    },
    {
      id: "product",
      title: "Продукт и аргументация",
      paragraphs: [
        "Состав, активные компоненты и применение подкреплены научной аргументацией и блоком «до/после».",
      ],
      bullets: ["Состав и активные компоненты", "Применение", "Блок «до/после»"],
    },
    {
      id: "purchase",
      title: "Переход к покупке",
      paragraphs: [
        "Сайт работает как брендовая витрина: пользователь направляется на доступные внешние каналы покупки.",
      ],
      bullets: ["Переходы на внешние площадки", "Переходы в магазины"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка сайта", to: routes.corporate },
    ],
  },
};
